import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const BetaBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { themeMode, jamaicanColors } = useTheme();
  const location = useLocation();

  // Handle banner visibility based on location and dismissal state
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Only show banner on homepage
    if (currentPath === '/') {
      // Check if banner was dismissed in current session
      const isDismissedInSession = sessionStorage.getItem('betaBannerDismissedThisSession');
      
      if (isDismissedInSession === 'true') {
        // Banner was dismissed in this session, but show it again on homepage navigation
        sessionStorage.removeItem('betaBannerDismissedThisSession');
        setIsVisible(true);
      } else {
        // First time on homepage or coming from other page
        setIsVisible(true);
      }
    } else {
      // Not on homepage, hide banner
      setIsVisible(false);
    }
  }, [location.pathname]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Mark as dismissed for this session, but allow it to show again on homepage navigation
    sessionStorage.setItem('betaBannerDismissedThisSession', 'true');
  };

  const handleFeedbackClick = () => {
    window.location.href = 'mailto:support@jamdungjobs.com?subject=JamDung Jobs Feedback';
  };

  // Only render banner if visible and on homepage
  if (!isVisible || location.pathname !== '/') return null;

  return (
    <Box
      className="beta-banner"
      sx={{
        position: 'fixed',
        top: { xs: '64px', sm: '72px' }, // Position below main navigation
        left: 0,
        right: 0,
        width: '100%',
        margin: 0,
        padding: 0,
        zIndex: 1400, // Below main navigation (1500)
        background: themeMode === 'light'
          ? `linear-gradient(135deg, ${jamaicanColors.gold} 0%, ${jamaicanColors.goldDark} 100%)`
          : `linear-gradient(135deg, ${jamaicanColors.goldDark} 0%, ${jamaicanColors.gold} 100%)`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        py: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        animation: 'slideDown 0.3s ease-out',
        '@keyframes slideDown': {
          from: {
            transform: 'translateY(-100%)'
          },
          to: {
            transform: 'translateY(0)'
          }
        }
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: { xs: 'nowrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 2 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: { xs: 1, sm: 2 } }}>
          <FeedbackIcon 
            sx={{ 
              color: jamaicanColors.black, 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              flexShrink: 0
            }} 
          />
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: jamaicanColors.black,
                fontWeight: 700,
                fontSize: { xs: '0.875rem', sm: '1.125rem' },
                lineHeight: 1.2,
                mb: 0.5
              }}
            >
              BETA PREVIEW - Your Feedback Helps Build Jamaica's Job Platform
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: jamaicanColors.black,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              We're working to create opportunities for all Jamaicans.{' '}
              <Link
                component="button"
                onClick={handleFeedbackClick}
                sx={{
                  color: jamaicanColors.darkGreen,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': {
                    color: jamaicanColors.green
                  }
                }}
              >
                Share your thoughts
              </Link>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleFeedbackClick}
            sx={{
              backgroundColor: jamaicanColors.green,
              color: '#ffffff',
              display: { xs: 'none', sm: 'flex' },
              '&:hover': {
                backgroundColor: jamaicanColors.darkGreen
              }
            }}
            startIcon={<FeedbackIcon />}
          >
            Give Feedback
          </Button>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: jamaicanColors.black,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.1)'
              }
            }}
            aria-label="Close beta banner"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default BetaBanner;
